import { useState, useEffect } from "react";
import { NoteField } from "shared/editors/note-field";
import { UploadField } from "shared/editors/upload-field";
import { DocumentReferenceResponse, GetProjectResponse, useDeleteApiProjectsByProjectIdFoldersAndFolderIdDocumentsDocumentIdMutation, useGetApiConfigQuery } from "api/generatedApi";
import { UploadDocument } from "shared/editors/hooks/upload-document";
import { makeFullPath } from "api/path";
import { useFolderRestrictions } from "shared/user-restrictions/use-folder-restrictions";
import { Box, Card, Divider } from "@mui/material";
import { t } from "i18next";
import { ExtendedProjectFolder } from "pages/project/hooks/use-map-tree-to-flat";
import { useToast } from "shared/toast/hooks/use-toast";
import { useScreenSize } from "shared/use-screen-size";
import { useDocumentationModal } from "components/page/folder/content/overview/documentation/hooks/use-documentation-modal";

export interface DocumentationComponentProps {
  project: GetProjectResponse;
  folder: ExtendedProjectFolder;
  note?: string | null;
  documents?: DocumentReferenceResponse[];
}

export const DocumentationComponent = (props: DocumentationComponentProps) => {
  const { project, folder } = props;
  const { canEditNote, canDeleteDocument, canUploadeDocument, canDownloadDocument } = useFolderRestrictions(project);
  const [note, setNote] = useState<string | null>(folder?.projectFolderDescription || null);
  const [documents, setDocuments] = useState<DocumentReferenceResponse[]>(props.documents || []);
  const disableNoteEdit = true;
  const disableNoteButton = false;
  const disableDocument = true;
  const { data: configData } = useGetApiConfigQuery();
  const path = makeFullPath(`/api/projects/${project.id}/folders/${folder.projectFolderId}/documents`);
  const [fileUploading, setFileUploading] = useState(false);
  const toast = useToast();
  const clickUploadDocument = UploadDocument(path, configData?.maxUploadFileSizeMB);
  const { screenSize } = useScreenSize();
  const [deleteDocument] = useDeleteApiProjectsByProjectIdFoldersAndFolderIdDocumentsDocumentIdMutation();

  useEffect(() => {
    setDocuments(folder.documents ?? []);
    setNote(folder.projectFolderDescription ?? "");
  }, [folder.documents, folder.projectFolderDescription]);

  const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNote(e.target.value);
  };

  //delete file
  const clickDocumentDelete = async (document: DocumentReferenceResponse) => {
    try {
      await deleteDocument({
        projectId: project.id ? project.id : "",
        folderId: folder?.projectFolderId ? folder.projectFolderId : "",
        documentId: document.documentId ? document.documentId : "",
      }).unwrap();
      const res = documents.filter((doc) => doc.documentId !== document.documentId);
      setDocuments(res);
      toast.success(t("content.overview.documents.documentDeleted", { filename: document.name }));
    } catch (e) {
      toast.error(t("content.overview.documents.deleteError", { documentName: document.name }));
    }
  };

  //upload file
  const handleFileChange = async (file: File) => {
    setFileUploading(true);
    await clickUploadDocument(file)
      .then((filename) => {
        if (!filename || filename === "") throw new Error();
        toast.success(t("content.overview.documents.documentUploaded", { filename }));
      })
      .catch((error: unknown) => {
        if (error instanceof RangeError) {
          toast.error(t("content.overview.documents.uploadFileSizeError", { maxFileSize: configData?.maxUploadFileSizeMB ?? 25 }));
        } else {
          toast.error(t("content.overview.documents.uploadError"));
          console.error(error);
        }
      })
      .finally(() => {
        setFileUploading(false);
        setDocuments(folder.documents ?? []);
      });
  };

  const openDocumentationModal = useDocumentationModal({
    project,
    folder,
    documents,
    handleFileChange: handleFileChange,
    fileUploading,
    onChange: handleNoteChange,
    canDeleteDocument: canDeleteDocument,
    clickDocumentDelete: clickDocumentDelete,
  });

  const onNoteClick = () => {
    openDocumentationModal();
  };

  return (
    <Card variant="outlined" sx={{ maxHeight: "840px", minHeight: "420px" }}>
      <NoteField
        value={note}
        onChange={handleNoteChange}
        label="Note"
        onButtonClick={onNoteClick}
        disableEdit={disableNoteEdit}
        disableButton={disableNoteButton}
        noContentText={t("content.overview.description.noDescription")}
        screenSize={screenSize}
        canEditNote={canEditNote()}
        textColor="primary.black"
      />
      <Box sx={{ width: "90%", mx: "auto" }}>
        <Divider />
      </Box>
      <UploadField
        allowDownloads={canDownloadDocument()}
        canUploadDocument={canUploadeDocument}
        isUploading={fileUploading}
        documents={documents}
        disabled={disableDocument}
        title="Dokumenter"
        noContentText={t("content.overview.documents.noDocuments")}
        screenSize={screenSize}
        canDeleteDocument={canDeleteDocument}
        textColor="primary.black"
      />
    </Card>
  );
};
